import { isObject } from "radashi";

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

type SetUnchangedToNullOptions<T> = {
  ignoreKeys?: (keyof T)[];
};

/**
 * Replaces unchanged values in an object with `null`, compared against a given default object.
 *
 * This is useful when persisting user settings: if a setting matches its default,
 * it is set to `null` so that when reloading, the default will be used automatically.
 *
 * - For primitive values: if equal to the default, set to `null`; otherwise keep as is.
 * - For nested objects: apply the same logic recursively.
 * - For keys not present in the default, values are preserved.
 *
 * @template T - The type of the input object.
 * @param submission - The object containing the user’s submitted values.
 * @param defaultValue - The object containing the default values for comparison.
 * @returns A new object where unchanged values are replaced with `null`.
 *
 * @example
 * const submission = { theme: "dark", volume: 5 };
 * const defaults = { theme: "light", volume: 5 };
 *
 * const result = setUnchangedToNull(submission, defaults);
 * // result -> { theme: "dark", volume: null }
 */
export function setUnchangedToNull<T extends object>(
  submission: T,
  defaultValue: T,
  options?: SetUnchangedToNullOptions<T>,
) {
  const result: Partial<Nullable<T>> = {};
  const ignoreKeys = options?.ignoreKeys || [];
  for (const key in submission) {
    if (ignoreKeys.includes(key)) {
      result[key] = submission[key];
      continue;
    }

    const value = submission[key];
    const defaultVal = defaultValue[key];

    if (isObject(value) && isObject(defaultVal)) {
      result[key] = setUnchangedToNull(value, defaultVal);
    } else if (value === defaultVal) {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }
  return result as T;
}
